import { useState } from 'react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import WhatsAppFloat from '../../../components/ui/WhatsAppFloat';

const ReparacionCreditoPage = () => {
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('beneficios');



  const faqs = [
    {
      question: '¿Cuánto tiempo toma ver resultados en mi score crediticio?',
      answer: 'Los primeros resultados suelen verse entre 30-45 días después de iniciar las disputas. El proceso completo puede tomar entre 3-8 meses dependiendo de la complejidad de tu situación crediticia y el plan elegido.'
    },
    {
      question: '¿Qué garantías ofrecen en el proceso de reparación?',
      answer: 'Ofrecemos garantías de satisfacción de 90 a 180 días según el plan. Si no ves mejoras significativas en tu score crediticio durante este período, te devolvemos tu dinero completo.'
    },
    {
      question: '¿Pueden eliminar bancarrotas o embargos de mi reporte?',
      answer: 'Podemos disputar cualquier elemento negativo en tu reporte crediticio, incluyendo bancarrotas y embargos. Sin embargo, solo podemos eliminar elementos que sean inexactos, incompletos o no verificables según las leyes federales.'
    },
    {
      question: '¿Necesito tener un score mínimo para comenzar el proceso?',
      answer: 'No hay score mínimo requerido. Hemos ayudado a clientes con scores desde 350 hasta 650. Mientras más bajo sea tu score inicial, mayor potencial de mejora tendrás.'
    },
    {
      question: '¿Qué diferencia hay entre reparación y construcción de crédito?',
      answer: 'La reparación se enfoca en eliminar elementos negativos existentes, mientras que la construcción agrega nuevas líneas de crédito positivas. Nuestros planes premium incluyen ambas estrategias para resultados máximos.'
    },
    {
      question: '¿Puedo obtener préstamos mientras reparo mi crédito?',
      answer: 'Sí, te enseñamos estrategias para obtener crédito incluso durante el proceso de reparación. Esto incluye tarjetas aseguradas, préstamos de constructor de crédito y líneas comerciales.'
    }
  ];



  const benefits = [
    {
      icon: 'ri-line-chart-line',
      title: 'Incremento Garantizado del Score',
      description: 'Incremento promedio de 150+ puntos en tu score FICO en 3-6 meses con nuestras estrategias probadas.'
    },
    {
      icon: 'ri-delete-bin-line',
      title: 'Eliminación de Elementos Negativos',
      description: 'Disputamos y eliminamos cuentas en cobranza, pagos tardíos, bancarrotas y otros elementos dañinos.'
    },
    {
      icon: 'ri-bank-card-line',
      title: 'Acceso a Nuevo Crédito',
      description: 'Te ayudamos a obtener nuevas tarjetas de crédito y líneas comerciales durante el proceso de reparación.'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Ahorro en Intereses',
      description: 'Un mejor score te permite acceder a tasas de interés más bajas, ahorrando miles de dólares anualmente.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Protección Legal Completa',
      description: 'Utilizamos todas las leyes federales de protección al consumidor para defender tus derechos crediticios.'
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Educación Crediticia',
      description: 'Aprende a mantener y mejorar tu crédito de por vida con nuestro programa educativo completo.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Análisis Inicial',
      description: 'Revisamos tus 3 reportes crediticios y identificamos todos los elementos negativos que pueden ser disputados.',
      duration: '24-48 horas'
    },
    {
      step: '02',
      title: 'Estrategia Personalizada',
      description: 'Creamos un plan específico basado en tu situación única y objetivos financieros.',
      duration: '2-3 días'
    },
    {
      step: '03',
      title: 'Disputas Profesionales',
      description: 'Enviamos cartas de disputa profesionales a las agencias de crédito y acreedores.',
      duration: 'Continuo'
    },
    {
      step: '04',
      title: 'Seguimiento y Optimización',
      description: 'Monitoreamos los resultados y ajustamos la estrategia para maximizar las mejoras.',
      duration: 'Mensual'
    },
    {
      step: '05',
      title: 'Construcción de Crédito',
      description: 'Agregamos nuevas líneas de crédito positivas para acelerar el crecimiento de tu score.',
      duration: 'Continuo'
    }
  ];

  const creditFactors = [
    { factor: 'Historial de Pagos', percentage: 35, description: 'El factor más importante en tu score crediticio' },
    { factor: 'Utilización de Crédito', percentage: 30, description: 'Mantener balances bajos es crucial' },
    { factor: 'Historial Crediticio', percentage: 15, description: 'Cuentas más antiguas mejoran tu score' },
    { factor: 'Tipos de Crédito', percentage: 10, description: 'Diversidad en tipos de cuentas' },
    { factor: 'Consultas Recientes', percentage: 10, description: 'Nuevas aplicaciones pueden bajar tu score' }
  ];

  return (
    <div className="min-h-screen bg-black pt-20">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-black via-gray-900 to-black py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-vip-gold/10 to-transparent"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6">
                  Reparación de <span className="bg-gradient-to-r from-vip-gold to-vip-gold-light bg-clip-text text-transparent">Crédito</span>
                </h1>
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                  Transforma tu score crediticio y accede a las mejores oportunidades financieras. Incremento promedio de 150+ puntos garantizado.
                </p>

                <div className="grid grid-cols-3 gap-6 mb-8">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-vip-gold mb-2">2,500+</div>
                    <div className="text-gray-300 text-sm">Clientes Exitosos</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-vip-gold mb-2">150+</div>
                    <div className="text-gray-300 text-sm">Puntos Promedio</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-vip-gold mb-2">100%</div>
                    <div className="text-gray-300 text-sm">Tasa de Éxito</div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href="https://web.cashflow.vip/widget/booking/dZafhFlPIO82YbvGywB0"
                    className="bg-gradient-to-r from-vip-gold to-vip-gold-light text-black px-8 py-4 rounded-full font-bold text-lg hover:shadow-lg hover:shadow-vip-gold/30 transition-all duration-300 whitespace-nowrap text-center"
                  >
                    Evaluación Gratuita
                  </a>
                  <a
                    href="https://www.skool.com/cashflow"
                    className="border-2 border-vip-gold text-vip-gold px-8 py-4 rounded-full font-bold text-lg hover:bg-vip-gold hover:text-black transition-all duration-300 whitespace-nowrap text-center"
                  >
                    Ver Comunidad
                  </a>
                </div>
              </div>

              <div className="relative">
                <img
                  src="/images/878e867da1b36d45914141269b250482.webp"
                  alt="Reparación de Crédito"
                  className="w-full h-auto rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-gray-800 border border-gray-700 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <i className="ri-line-chart-line text-2xl text-vip-gold"></i>
                    <div>
                      <div className="font-bold text-white">Score Mejorado</div>
                      <div className="text-sm text-gray-400">+150 Puntos Promedio</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Credit Score Factors */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              ¿Qué Afecta tu <span className="text-vip-gold">Score Crediticio?</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Entender estos factores es clave para mejorar y mantener un excelente score crediticio
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {creditFactors.map((factor, index) => (
              <div key={index} className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-white">{factor.factor}</h3>
                  <span className="text-2xl font-bold text-vip-gold">{factor.percentage}%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-3 mb-3">
                  <div
                    className="bg-gradient-to-r from-vip-gold to-vip-gold-light h-3 rounded-full transition-all duration-1000"
                    style={{ width: `${factor.percentage}%` }}
                  ></div>
                </div>
                <p className="text-gray-300">{factor.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Beneficios de la <span className="text-vip-gold">Reparación</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Un crédito excelente abre puertas a oportunidades financieras ilimitadas
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl hover:shadow-vip-gold/20 transition-all duration-300 border border-gray-700 group">
                <div className="w-16 h-16 bg-gradient-to-br from-vip-gold to-vip-gold-light rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${benefit.icon} text-2xl text-black`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{benefit.title}</h3>
                <p className="text-gray-300">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Nuestro <span className="text-vip-gold">Proceso</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Un método sistemático y probado para reparar tu crédito de manera efectiva
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-5 gap-8">
              {process.map((step, index) => (
                <div key={index} className="text-center group">
                  <div className="relative mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-vip-gold to-vip-gold-light rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                      <span className="text-2xl font-bold text-black">{step.step}</span>
                    </div>
                    {index < process.length - 1 && (
                      <div className="hidden md:block absolute top-10 left-full w-full h-0.5 bg-gray-700"></div>
                    )}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-3">{step.title}</h3>
                  <p className="text-gray-300 text-sm mb-2">{step.description}</p>
                  <div className="text-xs text-vip-gold font-medium">{step.duration}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>



      {/* FAQ Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Preguntas <span className="text-vip-gold">Frecuentes</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Resolvemos las dudas más comunes sobre reparación de crédito
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
                <button
                  onClick={() => setSelectedPlan(selectedPlan === `faq-${index}` ? null : `faq-${index}`)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-750 transition-colors"
                >
                  <h3 className="font-bold text-white">{faq.question}</h3>
                  <i className={`ri-arrow-${selectedPlan === `faq-${index}` ? 'up' : 'down'}-s-line text-vip-gold text-xl`}></i>
                </button>
                {selectedPlan === `faq-${index}` && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-300">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-black via-gray-900 to-black">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-6xl font-bold text-white mb-6">
            ¿Listo para <span className="bg-gradient-to-r from-vip-gold to-vip-gold-light bg-clip-text text-transparent">Transformar</span> tu Crédito?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            No esperes más. Tu libertad financiera comienza con un crédito excelente. Agenda tu evaluación gratuita hoy.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-12">
            <a
              href="https://web.cashflow.vip/widget/booking/dZafhFlPIO82YbvGywB0"
              className="bg-gradient-to-r from-vip-gold to-vip-gold-light text-black px-10 py-5 rounded-full font-bold text-xl hover:shadow-lg hover:shadow-vip-gold/30 transition-all duration-300 whitespace-nowrap"
            >
              Evaluación Gratuita Ahora
            </a>
            <a
              href="https://www.skool.com/cashflow"
              className="border-2 border-vip-gold text-vip-gold px-10 py-5 rounded-full font-bold text-xl hover:bg-vip-gold hover:text-black transition-all duration-300 whitespace-nowrap"
            >
              Unirse a la Comunidad
            </a>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { icon: 'ri-shield-check-line', text: 'Garantía de Satisfacción' },
              { icon: 'ri-time-line', text: 'Resultados en 30-45 Días' },
              { icon: 'ri-customer-service-line', text: 'Soporte Especializado' }
            ].map((item, index) => (
              <div key={index} className="flex items-center justify-center gap-3 text-white">
                <i className={`${item.icon} text-2xl text-vip-gold`}></i>
                <span className="font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default ReparacionCreditoPage;